import { useState, useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from '../../../contexts/WebSocketContext';
import { TelemetrySample, Alert, WebSocketMessage } from '@cbm/shared';

// Generic real-time data hook
export function useRealTimeData<T>(
  initialData: T,
  messageFilter: (message: WebSocketMessage) => boolean,
  dataExtractor: (message: WebSocketMessage) => Partial<T> | null,
  merger: (current: T, update: Partial<T>) => T
) {
  const [data, setData] = useState<T>(initialData);
  const { lastMessage, isConnected } = useWebSocket();
  const dataRef = useRef(data);

  // Keep ref in sync with state
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Process incoming messages
  useEffect(() => {
    if (!lastMessage || !isConnected) return;

    if (messageFilter(lastMessage)) {
      const update = dataExtractor(lastMessage);
      if (update) {
        setData(current => merger(current, update));
      }
    }
  }, [lastMessage, isConnected, messageFilter, dataExtractor, merger]);

  // Reset data when connection is lost
  useEffect(() => {
    if (!isConnected) {
      setData(initialData);
    }
  }, [isConnected, initialData]);

  return {
    data,
    isConnected,
    setData,
  };
}

// Machine telemetry data hook
interface TelemetryData {
  samples: TelemetrySample[];
  lastUpdate: string | null;
  machineIds: Set<string>;
}

export function useMachineTelemetry(maxSamples: number = 100) {
  const { subscribeToMachine, unsubscribeFromMachine } = useWebSocket();
  const subscribedMachines = useRef<Set<string>>(new Set());

  const { data, isConnected, setData } = useRealTimeData<TelemetryData>(
    {
      samples: [],
      lastUpdate: null,
      machineIds: new Set(),
    },
    (message) => message.type === 'telemetry_update',
    (message) => {
      if (message.type === 'telemetry_update') {
        const { sample } = message.payload;
        return {
          samples: [sample],
          lastUpdate: sample.timestamp,
          machineIds: new Set([sample.machineId]),
        };
      }
      return null;
    },
    (current, update) => {
      if (!update.samples) return current;

      const newSample = update.samples[0];
      const updatedSamples = [newSample, ...current.samples].slice(0, maxSamples);
      const updatedMachineIds = new Set([...current.machineIds, ...update.machineIds!]);

      return {
        samples: updatedSamples,
        lastUpdate: update.lastUpdate || current.lastUpdate,
        machineIds: updatedMachineIds,
      };
    }
  );

  // Subscribe to machine telemetry
  const subscribe = useCallback((machineId: string) => {
    if (!subscribedMachines.current.has(machineId)) {
      subscribeToMachine(machineId);
      subscribedMachines.current.add(machineId);
    }
  }, [subscribeToMachine]);

  // Unsubscribe from machine telemetry
  const unsubscribe = useCallback((machineId: string) => {
    if (subscribedMachines.current.has(machineId)) {
      unsubscribeFromMachine(machineId);
      subscribedMachines.current.delete(machineId);
    }
  }, [unsubscribeFromMachine]);

  // Unsubscribe from all machines on unmount
  useEffect(() => {
    return () => {
      subscribedMachines.current.forEach(machineId => {
        unsubscribeFromMachine(machineId);
      });
      subscribedMachines.current.clear();
    };
  }, [unsubscribeFromMachine]);

  // Get samples for specific machine
  const getSamplesForMachine = useCallback((machineId: string) => {
    return data.samples.filter(sample => sample.machineId === machineId);
  }, [data.samples]);

  // Get latest sample for machine
  const getLatestSampleForMachine = useCallback((machineId: string) => {
    const samples = getSamplesForMachine(machineId);
    return samples.length > 0 ? samples[0] : null;
  }, [getSamplesForMachine]);

  return {
    samples: data.samples,
    lastUpdate: data.lastUpdate,
    machineIds: Array.from(data.machineIds),
    isConnected,
    subscribe,
    unsubscribe,
    getSamplesForMachine,
    getLatestSampleForMachine,
    clearData: () => setData({
      samples: [],
      lastUpdate: null,
      machineIds: new Set(),
    }),
  };
}

// Alert notifications hook
interface AlertData {
  alerts: Alert[];
  newAlerts: Alert[];
  updatedAlerts: Alert[];
  lastUpdate: string | null;
}

export function useAlertNotifications(maxAlerts: number = 50) {
  const { data, isConnected, setData } = useRealTimeData<AlertData>(
    {
      alerts: [],
      newAlerts: [],
      updatedAlerts: [],
      lastUpdate: null,
    },
    (message) => message.type === 'alert_created' || message.type === 'alert_updated',
    (message) => {
      if (message.type === 'alert_created' || message.type === 'alert_updated') {
        const { alert } = message.payload;
        return {
          alerts: [alert],
          newAlerts: message.type === 'alert_created' ? [alert] : [],
          updatedAlerts: message.type === 'alert_updated' ? [alert] : [],
          lastUpdate: new Date().toISOString(),
        };
      }
      return null;
    },
    (current, update) => {
      if (!update.alerts) return current;

      const newAlert = update.alerts[0];
      const existingIndex = current.alerts.findIndex(a => a.alertId === newAlert.alertId);
      
      let updatedAlerts: Alert[];
      if (existingIndex >= 0) {
        // Update existing alert
        updatedAlerts = [...current.alerts];
        updatedAlerts[existingIndex] = newAlert;
      } else {
        // Add new alert
        updatedAlerts = [newAlert, ...current.alerts].slice(0, maxAlerts);
      }

      return {
        alerts: updatedAlerts,
        newAlerts: [...current.newAlerts, ...update.newAlerts!],
        updatedAlerts: [...current.updatedAlerts, ...update.updatedAlerts!],
        lastUpdate: update.lastUpdate || current.lastUpdate,
      };
    }
  );

  // Clear new/updated alert notifications
  const clearNotifications = useCallback(() => {
    setData(current => ({
      ...current,
      newAlerts: [],
      updatedAlerts: [],
    }));
  }, [setData]);

  // Get alerts for specific machine
  const getAlertsForMachine = useCallback((machineId: string) => {
    return data.alerts.filter(alert => alert.machineId === machineId);
  }, [data.alerts]);

  // Get alerts by status
  const getAlertsByStatus = useCallback((status: Alert['status']) => {
    return data.alerts.filter(alert => alert.status === status);
  }, [data.alerts]);

  // Get alerts by severity
  const getAlertsBySeverity = useCallback((severity: Alert['severity']) => {
    return data.alerts.filter(alert => alert.severity === severity);
  }, [data.alerts]);

  return {
    alerts: data.alerts,
    newAlerts: data.newAlerts,
    updatedAlerts: data.updatedAlerts,
    lastUpdate: data.lastUpdate,
    isConnected,
    clearNotifications,
    getAlertsForMachine,
    getAlertsByStatus,
    getAlertsBySeverity,
    clearData: () => setData({
      alerts: [],
      newAlerts: [],
      updatedAlerts: [],
      lastUpdate: null,
    }),
  };
}

// Combined machine data hook
export function useMachineData(machineId: string, options?: {
  maxTelemetrySamples?: number;
  maxAlerts?: number;
  autoSubscribe?: boolean;
}) {
  const {
    maxTelemetrySamples = 100,
    maxAlerts = 50,
    autoSubscribe = true,
  } = options || {};

  const telemetry = useMachineTelemetry(maxTelemetrySamples);
  const alerts = useAlertNotifications(maxAlerts);

  // Auto-subscribe to machine telemetry
  useEffect(() => {
    if (autoSubscribe && machineId) {
      telemetry.subscribe(machineId);
      return () => telemetry.unsubscribe(machineId);
    }
  }, [machineId, autoSubscribe, telemetry]);

  // Get machine-specific data
  const machineTelemetry = telemetry.getSamplesForMachine(machineId);
  const machineAlerts = alerts.getAlertsForMachine(machineId);
  const latestSample = telemetry.getLatestSampleForMachine(machineId);

  return {
    machineId,
    telemetry: machineTelemetry,
    alerts: machineAlerts,
    latestSample,
    isConnected: telemetry.isConnected && alerts.isConnected,
    newAlerts: alerts.newAlerts.filter(alert => alert.machineId === machineId),
    updatedAlerts: alerts.updatedAlerts.filter(alert => alert.machineId === machineId),
    clearNotifications: alerts.clearNotifications,
    subscribe: telemetry.subscribe,
    unsubscribe: telemetry.unsubscribe,
  };
}